/**
 * The Agenda's panel for one repeating schedule: its rule, robot, next
 * runs and who made it, with what this viewer may do to it. It floats
 * where the task panel does (`LanePanel`, whose look it shares) and
 * closes the same way (× or Esc, `CalendarLanes`).
 *
 * NO NEW PERMISSIONS. The actions are the phone's (`useScheduleActions`):
 * each is present only when the screen lets this viewer edit schedules,
 * drawn but off in a "View as" preview (with the preview line), and "Run
 * next now" is held -- and says so -- while fire mode holds the site.
 *
 * "Edit…" (2026-10-30) opens the schedule editor (`ScheduleEditDrawer`),
 * under the same gate: the screen hands `onEdit` in only when this
 * viewer may edit schedules, and never on a phone. Fire mode does not
 * hold it -- an edit changes a plan and dispatches nothing. A one-off
 * booking has no Edit: it is a date, moved on the lanes.
 *
 * Who made it is named (`creatorLabel`): "you", their name, their
 * address, or "a team member" -- never a raw id. Who CHANGED it is the
 * Audit screen's question; this panel does not try to answer it.
 *
 * A schedule an edit split in two -- the old time kept on a second
 * schedule of the same name, and the new time after it -- asks before
 * Pause, from either row, whether the other pauses too, and before
 * Resume whether the other, paused too, resumes (`planPartners`,
 * `KeptPauseConfirm`): either alone would leave the other as it was.
 *
 * A schedule that has ended (shown "Ended" when its panel was already
 * open) has no Pause or Resume -- nothing of it is left to run -- but
 * keeps Edit, the way to move a mistaken end date later.
 */
import { useState, type ReactNode } from 'react';
import type { ScheduleOccurrence, TaskSchedule } from '../../../api/v1';
import { isOneShotRule } from '../rrule';
import { formatZonedClock, formatZonedDate } from '../zoned-time';
import { NEXT_RUNS_SHOWN, noRunText, schedulePaint, upcomingRuns } from './agenda-model';
import { laneVars, paintLabel } from './lane-style';
import { KeptPauseConfirm } from './KeptPauseConfirm';
import { PanelStatus } from './PanelStatus';
import { useRevealOnMount } from './reveal';
import { creatorLabel } from './people';
import { nextRunText, runNamed, type ScheduleRow } from './phone-model';
import { planPartners, repeatsLine } from './schedule-span';
import { FIRE_HOLD_LINE, PREVIEW_LINE, type ScheduleActions } from './useScheduleActions';
import { isHighPriority } from '../../../api/task-priority';

export interface SchedulePanelProps {
  row: ScheduleRow | null;
  /** Every schedule on the site, for the other rows of this one's plan
   *  (`planPartners`). */
  schedules: readonly TaskSchedule[];
  occurrences: readonly ScheduleOccurrence[];
  nowMs: number;
  timeZone: string;
  horizonDays: number;
  actions: ScheduleActions;
  /** Robots whose deliveries are linen runs (`linenRobotKeys`). */
  linen: ReadonlySet<string>;
  /** The signed-in user's subject, to say "by you". */
  mySub: string | null;
  /** Open the schedule editor; absent when this viewer may not edit
   *  schedules, or on a phone. */
  onEdit?: (trigger: HTMLElement) => void;
  onClose: () => void;
  /** Select one of its runs in the Agenda (its task panel). */
  onSelectRun?: (o: ScheduleOccurrence) => void;
}

function Kv({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="nx-lane-panel__kv">
      <span className="nx-lane-panel__k">{k}</span>
      <span className="nx-lane-panel__v">{v}</span>
    </div>
  );
}

function Close({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      className="nx-lane-panel__close"
      onClick={onClose}
      aria-label="Close the panel (Esc)"
      title="Close (Esc)"
      data-testid="cal-panel-close"
    >
      ×
    </button>
  );
}

export function SchedulePanel(props: SchedulePanelProps) {
  const { row, actions, timeZone, nowMs } = props;
  const [confirmSkip, setConfirmSkip] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);
  if (!row) {
    return (
      <aside className="nx-lane-panel" aria-label="Selected schedule" data-testid="cal-panel">
        <div className="nx-lane-panel__top">
          <span className="nx-lane-panel__eyebrow">Repeating schedule</span>
          <Close onClose={props.onClose} />
        </div>
        <p className="nx-lane-panel__hint">This schedule is no longer on the site.</p>
      </aside>
    );
  }
  const s = row.schedule;
  const runs = row.active
    ? upcomingRuns(s.schedule_id, props.occurrences, nowMs, NEXT_RUNS_SHOWN)
    : [];
  const next = runs[0] ?? null;
  const busy = Boolean(actions.status?.pending);
  const off = actions.actionsOff || busy;
  // The other rows of its plan in the same state: what a Pause (or a
  // Resume) here alone would leave as they are.
  const partners = planPartners(s, props.schedules, nowMs, timeZone);
  const canToggle = Boolean(actions.setEnabled) && !row.ended;
  const canEdit = Boolean(props.onEdit) && !isOneShotRule(s.rrule);
  const anyAction = Boolean(actions.startNow || actions.skip || canToggle || canEdit);
  const paint = schedulePaint(row, props.linen);
  const repeats = repeatsLine(row.pattern, row.rule, row.span);
  // Named by the api-server from who has signed in (0.6.54); an older
  // server sends no name, and the label is then "you" or "a team member".
  const created =
    s.created_at_ms > 0
      ? `${formatZonedDate(s.created_at_ms, timeZone)} by ${creatorLabel(s, props.mySub)}`
      : null;

  return (
    <aside className="nx-lane-panel" aria-label="Selected schedule" data-testid="cal-panel">
      <div className="nx-lane-panel__top">
        <span className="nx-lane-panel__eyebrow">Repeating schedule</span>
        <Close onClose={props.onClose} />
      </div>
      <h3 className="nx-lane-panel__title" data-testid="cal-panel-title">
        {s.name}
      </h3>
      <span className="nx-lane-panel__type" style={laneVars(paint)}>
        <span className="nx-lane-panel__swatch" aria-hidden="true" />
        {paintLabel(paint)} · {row.robots}
      </span>
      <div className="nx-lane-panel__sep">
        {/* The end is the Runs line's to say, with its weekday. */}
        <Kv k={repeats.k} v={repeats.v} />
        {row.span && <Kv k="Runs" v={<span data-testid="sched-panel-span">{row.span}</span>} />}
        {row.places && <Kv k="Where" v={row.places} />}
        <Kv k="Robot" v={row.robots} />
        {/* Only for High: every plan is Normal unless somebody chose. */}
        {isHighPriority(s.task?.priority) && (
          <Kv k="Priority" v={<span data-testid="sched-panel-priority">High</span>} />
        )}
        <Kv
          k="Status"
          v={
            <span
              className={row.active && !row.ended ? '' : 'nx-agenda__paused'}
              data-testid="sched-panel-status"
            >
              {row.ended ? 'Ended' : row.active ? 'Active' : 'Paused'}
            </span>
          }
        />
        {created && <Kv k="Created" v={created} />}
      </div>

      {actions.actionsOff && anyAction && (
        <p className="nx-lane-panel__note is-hold" data-testid="sched-panel-preview">
          {PREVIEW_LINE}
        </p>
      )}

      {anyAction && (
        <div className="nx-lane-panel__actions" data-testid="sched-panel-actions">
          {actions.startNow && next && (
            <button
              type="button"
              className="nx-lane-btn nx-lane-btn--primary"
              disabled={off || actions.fireHold}
              title={
                actions.actionsOff
                  ? PREVIEW_LINE
                  : actions.fireHold
                    ? FIRE_HOLD_LINE
                    : `Send the ${formatZonedClock(next.start_ms, timeZone)} run to the fleet now instead of at its time.`
              }
              onClick={() => actions.startNow!(next)}
              data-testid="sched-panel-run-now"
            >
              Run next now
            </button>
          )}
          {actions.skip && next && !confirmSkip && (
            <button
              type="button"
              className="nx-lane-btn"
              disabled={off}
              title={
                actions.actionsOff ? PREVIEW_LINE : 'Skip just the next run; the schedule stays.'
              }
              onClick={() => setConfirmSkip(true)}
              data-testid="sched-panel-skip"
            >
              Skip next
            </button>
          )}
          {canToggle && (
            <button
              type="button"
              className="nx-lane-btn"
              disabled={off}
              title={
                actions.actionsOff
                  ? PREVIEW_LINE
                  : s.enabled
                    ? 'Stop every future run. Its times and history are kept.'
                    : 'Start running this schedule again from its next time.'
              }
              onClick={() =>
                partners ? setConfirmPause(true) : actions.setEnabled!(s.schedule_id, !s.enabled)
              }
              data-testid={s.enabled ? 'sched-panel-pause' : 'sched-panel-resume'}
            >
              {s.enabled ? 'Pause' : 'Resume'}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="nx-lane-btn"
              disabled={off}
              title={
                actions.actionsOff
                  ? PREVIEW_LINE
                  : 'Change what it does, when it runs, its name or its end.'
              }
              onClick={(e) => props.onEdit!(e.currentTarget)}
              data-testid="sched-panel-edit"
            >
              Edit…
            </button>
          )}
        </div>
      )}
      {actions.skip && next && confirmSkip && (
        <SkipConfirm
          run={runNamed(next.start_ms, nowMs, timeZone)}
          disabled={off}
          onKeep={() => setConfirmSkip(false)}
          onSkip={() => {
            setConfirmSkip(false);
            actions.skip!(next);
          }}
        />
      )}
      {canToggle && confirmPause && partners && (
        <KeptPauseConfirm
          text={partners.text}
          list={partners.list}
          count={partners.rows.length}
          verb={partners.verb}
          disabled={off}
          onBoth={() => {
            setConfirmPause(false);
            actions.setEnabled!(
              s.schedule_id,
              !s.enabled,
              partners.rows.map((k) => k.schedule_id),
            );
          }}
          onOne={() => {
            setConfirmPause(false);
            actions.setEnabled!(s.schedule_id, !s.enabled);
          }}
          onCancel={() => setConfirmPause(false)}
          className="nx-sched-panel__confirm"
          noteClass="nx-lane-panel__note"
          actionsClass="nx-lane-panel__actions"
          btnClass="nx-lane-btn"
          testId="sched-panel-pause-confirm"
        />
      )}
      {actions.startNow && next && actions.fireHold && (
        <p className="nx-lane-panel__note is-hold" data-testid="sched-panel-fire-hold">
          {FIRE_HOLD_LINE}
        </p>
      )}
      <PanelStatus status={actions.status} testId="sched-panel-status-line" />
      {row.active && !row.ended && (
        <div className="nx-lane-panel__sep" data-testid="sched-panel-runs">
          {/* Runs come from the occurrences already read, so a short list
              means "all there are in the days shown", not "the next five". */}
          <span className="nx-lane-panel__k">
            {runs.length >= NEXT_RUNS_SHOWN
              ? `Next ${NEXT_RUNS_SHOWN} runs`
              : `Runs in the next ${props.horizonDays} day${props.horizonDays === 1 ? '' : 's'}`}
          </span>
          {runs.length === 0 ? (
            <span className="nx-lane-panel__hint">{noRunText(props.horizonDays)}.</span>
          ) : (
            <ol className="nx-sched-panel__runs">
              {runs.map((o) => (
                <li key={`${o.schedule_id}:${o.original_start_ms}`}>
                  {props.onSelectRun ? (
                    <button
                      type="button"
                      className="nx-agenda__link"
                      onClick={() => props.onSelectRun!(o)}
                      data-testid={`sched-panel-run-${o.original_start_ms}`}
                    >
                      {nextRunText(o.start_ms, nowMs, timeZone)}
                    </button>
                  ) : (
                    nextRunText(o.start_ms, nowMs, timeZone)
                  )}
                  {o.overridden && <span className="nx-lane-panel__k"> · moved</span>}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
      {row.ended ? (
        <p className="nx-lane-panel__note" data-testid="sched-panel-ended">
          Ended{s.until_ms ? ` ${formatZonedDate(s.until_ms, timeZone)}` : ''}: it will not run
          again. Its past runs stay on the calendar.
        </p>
      ) : (
        !row.active && (
          <p className="nx-lane-panel__note">Paused: nothing will run until it is resumed.</p>
        )
      )}

    </aside>
  );
}

/** "Skip tomorrow's 09:00 run?" -- opened below the actions, which at
 *  1280x720 can be below what the panel shows: it scrolls into view and
 *  takes focus, on "Keep it" (`useRevealOnMount`). `run` is `runNamed`'s
 *  words: the next run need not be today's. */
function SkipConfirm(props: {
  run: string;
  disabled: boolean;
  onKeep: () => void;
  onSkip: () => void;
}) {
  const ref = useRevealOnMount<HTMLDivElement>();
  return (
    <div ref={ref} className="nx-sched-panel__confirm" role="group" aria-label="Confirm skip">
      <p className="nx-lane-panel__note">
        Skip {props.run}? Only that run is skipped; the schedule keeps running.
      </p>
      <div className="nx-lane-panel__actions">
        <button type="button" className="nx-lane-btn" onClick={props.onKeep}>
          Keep it
        </button>
        <button
          type="button"
          className="nx-lane-btn nx-lane-btn--danger"
          disabled={props.disabled}
          onClick={props.onSkip}
          data-testid="sched-panel-skip-confirm"
        >
          Skip that run
        </button>
      </div>
    </div>
  );
}
